<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useSearch } from "../../composables/useSearch.js";
import { getFileTypeLabel, getFileTypeBadgeClass } from "../../composables/useFileType.js";
import ProjectService from "../../services/projectService.js";
import ProjectCardMenu from "../../components/ui/ProjectCardMenu.vue";
import DocumentCardMenu from "../../components/ui/DocumentCardMenu.vue";
import projectCardImage from "../../../assets/images/projectCardImage.svg";

const router = useRouter();
const projectService = new ProjectService();

// Same labels/helpers Projects.vue uses for its grid card, kept in sync so a
// project card looks identical whether it's reached via browse or search.
const ROLE_LABELS = { OWNER: "Owner", EDITOR: "Editor", CONTRIBUTOR: "Contributor", VIEWER: "Viewer" };

function initials(person) {
  const label = person?.name || person?.email || "?";
  return label.slice(0, 2).toUpperCase();
}

function collaboratorAvatars(project) {
  const owner = project.ownerProfile ? [{ ...project.ownerProfile, role: "OWNER" }] : [];
  const others = (project.topCollaborators ?? [])
    .filter((c) => c.collaboratorRole !== "OWNER")
    .map((c) => ({ ...c.user, role: c.collaboratorRole }));
  return [...owner, ...others].slice(0, 3);
}

function overflowCount(project) {
  return Math.max(0, (project.totalCollaboratorCount ?? 0) - collaboratorAvatars(project).length);
}

function mimeBadge(doc) {
  return { label: getFileTypeLabel(doc.mimeType), class: getFileTypeBadgeClass(doc.mimeType) };
}

async function toggleFavorite(doc) {
  const isFavorite = !doc.isFavorite;
  doc.isFavorite = isFavorite; // optimistic
  try {
    await projectService.MarkDocumentFavorite({
      documentId: doc.id,
      isFavorite,
      email: localStorage.getItem("email"),
      tenantId: localStorage.getItem("tenantId"),
    });
  } catch (error) {
    doc.isFavorite = !isFavorite; // revert on failure
  }
}

function onDocumentDeleted(doc) {
  documentResults.value = documentResults.value.filter((d) => d.id !== doc.id);
}

const {
  inputValue, activeQuery, isSearching, hasSearched,
  recentSearches, projectResults, documentResults, textResults, annotationResults,
  search, resetSearch, removeRecent, clearRecent,
} = useSearch();

const activeTab = ref("projects");

const tabs = computed(() => [
  { key: "projects",    label: "Projects",    count: projectResults.value.length },
  { key: "documents",   label: "Documents",   count: documentResults.value.length },
  { key: "text",        label: "Text",        count: textResults.value.length },
  { key: "annotations", label: "Annotations", count: annotationResults.value.length },
]);

const tabResults = computed(() => {
  switch (activeTab.value) {
    case "projects":    return projectResults.value;
    case "documents":   return documentResults.value;
    case "text":        return textResults.value;
    case "annotations": return annotationResults.value;
    default:            return [];
  }
});

function runRecent(q) { inputValue.value = q; search(q); }

function openProject(p) {
  router.push({ name: "v2-project", params: { projectId: p.id } });
}
function openDocument(doc) {
  router.push({ name: "v2-document", params: { projectId: doc.projectId, docId: doc.id } });
}
function openTextResult(item) {
  router.push({ name: "v2-document", params: { projectId: item.projectId, docId: item.documentId }, query: { page: item.page + 1, q: activeQuery.value } });
}
function openAnnotationResult(item) {
  router.push({ name: "v2-document", params: { projectId: item.projectId, docId: item.documentId }, query: { page: item.page + 1, object: item.id } });
}

function timeAgo(ts) {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1)  return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  if (h < 48) return "Yesterday";
  return `${Math.floor(h / 24)} days ago`;
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}
</script>

<template>
  <div class="flex flex-col h-full bg-white dark:bg-slate-900">

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">

      <!-- ① Empty state (no history, no query) -->
      <div
        v-if="!hasSearched && !inputValue && recentSearches.length === 0"
        class="flex flex-col items-center justify-center h-full gap-5 py-20 px-6"
      >
        <div class="relative">
          <svg width="88" height="88" viewBox="0 0 88 88" fill="none" class="text-slate-200 dark:text-slate-700">
            <circle cx="44" cy="44" r="42" stroke="currentColor" stroke-width="2" />
            <circle cx="44" cy="44" r="30" stroke="currentColor" stroke-width="1.5" stroke-dasharray="5 4" />
            <circle cx="44" cy="44" r="16" stroke="currentColor" stroke-width="1.5" />
            <path d="M44 14 Q46 44 44 74" stroke="currentColor" stroke-width="1.5" />
            <path d="M14 44 Q44 46 74 44" stroke="currentColor" stroke-width="1.5" />
          </svg>
          <div class="absolute -bottom-1 -right-1 flex items-center justify-center w-10 h-10 bg-white dark:bg-slate-900 rounded-full border-2 border-slate-200 dark:border-slate-700">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="text-slate-400">
              <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
              <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </div>
        </div>
        <p class="text-sm text-slate-400 dark:text-slate-500 text-center max-w-xs leading-relaxed m-0">
          Search Folia for projects, documents, and annotations by typing a search query and pressing
          <kbd class="mx-0.5 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-[11px] text-slate-500 font-mono">↵</kbd>
        </p>
      </div>

      <!-- ② Recent searches (no active query, has history) -->
      <div v-else-if="!hasSearched && recentSearches.length > 0" class="px-5 py-5">
        <div class="flex items-center justify-between mb-3">
          <span class="text-[13px] font-semibold text-slate-600 dark:text-slate-300">Recent Searches</span>
          <button
            class="text-[12px] text-brand hover:opacity-80 border-0 bg-transparent cursor-pointer font-medium transition-opacity"
            @click="clearRecent"
          >
            Clear all
          </button>
        </div>

        <div class="flex flex-col">
          <div
            v-for="item in recentSearches"
            :key="item.query + item.timestamp"
            class="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 -mx-2 px-2 rounded-lg transition-colors group"
            @click="runRecent(item.query)"
          >
            <svg class="text-slate-400 dark:text-slate-500 shrink-0" width="15" height="15" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
              <path d="M12 7v5l3 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
            <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ item.query }}</span>
            <span class="text-[11px] text-slate-400 dark:text-slate-500 shrink-0">{{ timeAgo(item.timestamp) }}</span>
            <button
              class="opacity-0 group-hover:opacity-100 flex items-center justify-center w-5 h-5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-all shrink-0"
              @click.stop="removeRecent(item.query)"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- ③ + ④ Results or empty results -->
      <template v-else-if="hasSearched">

        <!-- Summary line -->
        <div class="px-5 pt-5 pb-2 shrink-0">
          <p class="text-sm text-slate-500 dark:text-slate-400 m-0">
            Search results for
            <span class="font-semibold text-slate-800 dark:text-slate-100">'{{ activeQuery }}'</span>
          </p>
        </div>

        <!-- Filter tabs -->
        <div class="flex items-center gap-0.5 px-4 border-b border-slate-200 dark:border-slate-700 overflow-x-auto shrink-0">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            :class="activeTab === tab.key
              ? 'text-brand border-brand'
              : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-200'"
            class="flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium border-b-2 border-0 bg-transparent cursor-pointer transition-colors whitespace-nowrap shrink-0"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
            <span
              v-if="tab.count > 0"
              :class="activeTab === tab.key ? 'bg-brand' : 'bg-slate-400 dark:bg-slate-600'"
              class="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-white text-[10px] font-bold rounded-full transition-colors"
            >{{ tab.count }}</span>
          </button>
        </div>

        <!-- Loading spinner -->
        <div v-if="isSearching" class="flex justify-center py-20">
          <svg class="animate-spin text-brand" width="32" height="32" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-dasharray="32" stroke-dashoffset="8" />
          </svg>
        </div>

        <!-- ④ Empty results for active tab -->
        <div
          v-else-if="tabResults.length === 0"
          class="flex flex-col items-center justify-center py-20 gap-4"
        >
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" class="text-slate-200 dark:text-slate-700">
            <circle cx="32" cy="20" r="13" stroke="currentColor" stroke-width="2" />
            <path d="M8 58c0-13.25 10.75-24 24-24s24 10.75 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <path d="M44 44l8 8M44 52l8-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <div class="text-center">
            <p class="text-sm font-semibold text-slate-600 dark:text-slate-300 m-0 mb-1">No results found</p>
            <p class="text-sm text-slate-400 dark:text-slate-500 m-0">
              We couldn't find any results for <span class="font-semibold">'{{ activeQuery }}'</span>
            </p>
          </div>
        </div>

        <!-- ③ Results -->
        <div v-else class="p-4 flex-1">

          <!-- Projects grid — matches Projects.vue's grid card exactly -->
          <div v-if="activeTab === 'projects'" class="grid grid-cols-[repeat(auto-fill,minmax(275px,1fr))] gap-4">
            <div
              v-for="p in projectResults"
              :key="p.id"
              class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden cursor-pointer hover:shadow-md dark:hover:border-slate-600 transition-all"
              @click="openProject(p)"
            >
              <div class="relative h-44 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                <img :src="projectCardImage" alt="" class="w-full h-full object-cover" />
                <span class="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 dark:bg-slate-800/90 text-[11px] font-medium text-slate-600 dark:text-slate-300 shadow-sm">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                    <circle cx="9" cy="8" r="3" stroke="currentColor" stroke-width="1.8" />
                    <path d="M2.5 19c0-3 2.9-5.5 6.5-5.5s6.5 2.5 6.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                  </svg>
                  {{ ROLE_LABELS[p.userRole] ?? p.userRole }}
                </span>
              </div>
              <div class="p-4">
                <div class="flex items-center justify-between gap-2">
                  <p class="text-base font-semibold text-slate-800 dark:text-slate-100 truncate m-0">{{ p.name }}</p>
                  <ProjectCardMenu :project="p" />
                </div>
                <p class="text-xs text-slate-400 dark:text-slate-500 m-0 mt-1">
                  {{ p.totalDocuments === 1 ? "1 document" : `${p.totalDocuments ?? 0} documents` }}
                </p>
                <p class="text-xs text-slate-400 dark:text-slate-500 m-0 mt-0.5">Last modified {{ formatDate(p.updatedAt) }}</p>
                <div class="flex items-center gap-1 mt-2.5">
                  <div
                    v-for="(person, index) in collaboratorAvatars(p)"
                    :key="person.email ?? index"
                    :style="{ marginLeft: index !== 0 ? '-8px' : '0' }"
                    class="flex items-center justify-center w-6 h-6 rounded-full bg-[#7ba7d4] text-white text-[10px] font-semibold border-2 border-white dark:border-slate-800"
                  >
                    {{ initials(person) }}
                  </div>
                  <span v-if="overflowCount(p) > 0" class="ml-1 text-[11px] text-slate-400 dark:text-slate-500">+{{ overflowCount(p) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Documents grid — matches Docs.vue's grid card exactly -->
          <div v-else-if="activeTab === 'documents'" class="grid grid-cols-[repeat(auto-fill,minmax(275px,1fr))] gap-4">
            <div
              v-for="doc in documentResults"
              :key="doc.id"
              class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden cursor-pointer hover:shadow-md dark:hover:border-slate-600 transition-all"
              @click="openDocument(doc)"
            >
              <div class="relative h-56 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                <img v-if="doc.preview" :src="doc.preview" class="h-40 w-auto max-w-[75%] object-contain shadow" />
                <button
                  class="absolute top-4 left-4 flex items-center justify-center text-slate-400 hover:text-yellow-500 dark:hover:text-yellow-400 border-0 bg-transparent cursor-pointer transition-colors"
                  @click.stop="toggleFavorite(doc)"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" :fill="doc.isFavorite ? 'currentColor' : 'none'" :class="doc.isFavorite ? 'text-yellow-400' : ''">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                  </svg>
                </button>
                <span class="absolute bottom-0 left-0 px-3 py-1.5 rounded-tr-lg text-xs font-bold text-white uppercase" :class="mimeBadge(doc).class">{{ mimeBadge(doc).label }}</span>
              </div>
              <div class="p-4">
                <div class="flex items-start justify-between gap-2">
                  <p class="text-xl font-bold text-slate-800 dark:text-slate-100 truncate m-0">{{ doc.name ?? "Untitled" }}</p>
                  <DocumentCardMenu v-if="doc.project" :document="doc" :project="doc.project" @deleted="onDocumentDeleted(doc)" />
                </div>
                <p class="text-sm text-slate-400 dark:text-slate-500 m-0 mt-1 truncate">
                  {{ doc.projectName ?? "No project" }} · Last modified {{ formatDate(doc.updatedAt) }}
                </p>
              </div>
            </div>
          </div>

          <!-- Text results -->
          <div v-else-if="activeTab === 'text'" class="flex flex-col gap-3">
            <div
              v-for="item in textResults"
              :key="item.id"
              class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 cursor-pointer hover:shadow-sm transition-shadow"
              @click="openTextResult(item)"
            >
              <div class="flex items-center gap-2 mb-2.5">
                <svg class="text-slate-400 shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M14 2v6h6M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                </svg>
                <span class="text-[12px] font-semibold text-slate-700 dark:text-slate-200 truncate">{{ item.documentName }}</span>
                <span class="text-[11px] text-slate-400 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded shrink-0">{{ item.projectName }}</span>
                <span class="ml-auto text-[11px] text-slate-400 dark:text-slate-500 shrink-0">p.{{ item.page + 1 }}</span>
              </div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p class="text-[13px] text-slate-600 dark:text-slate-300 m-0 leading-relaxed line-clamp-4" v-html="item.styledSnippet" />
            </div>
          </div>

          <!-- Annotations results -->
          <div v-else-if="activeTab === 'annotations'" class="flex flex-col gap-3">
            <div
              v-for="item in annotationResults"
              :key="item.id"
              class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 cursor-pointer hover:shadow-sm transition-shadow"
              @click="openAnnotationResult(item)"
            >
              <div class="flex items-center gap-2 mb-2.5">
                <svg class="text-amber-400 shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                <span class="text-[12px] font-semibold text-slate-700 dark:text-slate-200 truncate">{{ item.documentName }}</span>
                <span class="text-[11px] text-slate-400 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded shrink-0">{{ item.projectName }}</span>
                <span class="ml-auto text-[11px] text-slate-400 dark:text-slate-500 shrink-0">p.{{ item.page + 1 }}</span>
              </div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p class="text-[13px] text-slate-600 dark:text-slate-300 m-0 leading-relaxed" v-html="item.styledSnippet" />
            </div>
          </div>

        </div>
      </template>

    </div>
  </div>
</template>
